import { useEffect } from 'react';

import { useAtomValue } from 'jotai';
import { starredDealerListAtom } from '@lotlinx-aws/dealer-selector';

import { queryClient } from 'queryClient';

interface DealerListData {
  dealers: unknown[];
  favorites: number[];
}

/**
 * Keeps the React Query `dealerList` cache in sync with `starredDealerListAtom`.
 *
 * The dealer-selector package has a useEffect that overwrites the atom from the
 * query cache on every mount. Because the save-favorites mutation does not
 * invalidate the dealerList query, stale cached data can revert a star action
 * when the user switches accounts and reopens the dropdown.
 *
 * This hook patches the cached favorites whenever the atom changes so the
 * package's sync effect reads the correct value.
 */
export default function useSyncDealerFavorites(token: string | undefined) {
  const starredDealerList = useAtomValue(starredDealerListAtom);

  useEffect(() => {
    if (!token) return;

    const queryKey = ['dealerList', token];
    const cached = queryClient.getQueryData<DealerListData>(queryKey);

    if (!cached) return;

    const cachedFavorites = Array.isArray(cached.favorites) ? cached.favorites : [];
    const nextFavorites = Array.isArray(starredDealerList) ? starredDealerList : [];
    const isSame =
      cachedFavorites.length === nextFavorites.length &&
      cachedFavorites.every((id) => nextFavorites.includes(id));

    if (isSame) return;

    queryClient.setQueryData<DealerListData>(queryKey, {
      ...cached,
      favorites: nextFavorites,
    });
  }, [starredDealerList, token]);
}
